import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ValueConfidence } from './valueConfidence';

import { cn } from '@/lib/utils';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

/**
 * Why a figure that was already there is being replaced.
 *
 * Asked the moment the field is left, not when the form is saved. Two reasons, and the second is
 * the one that matters: at blur the change is still the thing the person is thinking about, whereas
 * at save time a batch of fields is presented back to somebody who has moved on. And one field at a
 * time can be answered; a list of six cannot, not honestly.
 *
 * Asked only for an overwrite, never when a field is filled for the first time: a first entry needs
 * no defence. What is worth recording is the moment a number that somebody — or the estimator —
 * already put there stops being the number.
 *
 * The old value is shown beside the new one because that is the question being answered. A comment
 * box on its own asks "why?"; the two values together ask "why not 500 any more?", which somebody
 * can actually answer.
 *
 * Not compulsory, and the second button says so plainly rather than hiding it in a cancel. A
 * required field here would be answered with a full stop, and a history full of full stops is worse
 * than one with gaps — it looks documented.
 */

export interface OverwriteEntry {
    name: string;
    label: string;
    /** As the reader sees them, already formatted and with the unit. */
    previous: string;
    next: string;
    confidence: ValueConfidence;
}

const CONFIDENCE_COLOR: Record<ValueConfidence, string> = {
    certain: 'bg-[#2F8F4E]',
    estimated: 'bg-[#D08A17]',
    missing: 'bg-[#C0392B]',
};

export default function OverwriteCommentDialog({
    entry,
    onDismiss,
    onConfirm,
}: {
    /** Null closes the dialog; the caller decides what counts as an overwrite. */
    entry: OverwriteEntry | null;
    onDismiss: () => void;
    onConfirm: (comment: string) => void;
}) {
    const { t } = useTranslation();
    const [comment, setComment] = useState('');

    // A second overwrite must not inherit the first one's reason.
    useEffect(() => setComment(''), [entry]);

    return (
        <Dialog open={!!entry} onOpenChange={(open) => (open ? null : onDismiss())}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t('portfolioDashboard.overwrite.title')}</DialogTitle>
                    <DialogDescription>{t('portfolioDashboard.overwrite.explanation')}</DialogDescription>
                </DialogHeader>

                {entry ? (
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span
                                className={cn(
                                    'block h-2.5 w-2.5 shrink-0 rounded-full',
                                    CONFIDENCE_COLOR[entry.confidence],
                                )}
                                aria-hidden
                            />
                            <span className="font-medium">{entry.label}</span>
                            <span className="text-muted-foreground line-through">{entry.previous}</span>
                            <span aria-hidden>→</span>
                            <span className="font-semibold">{entry.next}</span>
                        </div>

                        <Label htmlFor="overwriteComment" className="sr-only">
                            {t('portfolioDashboard.overwrite.comment')}
                        </Label>
                        <Textarea
                            id="overwriteComment"
                            rows={3}
                            autoFocus
                            className="min-h-0 resize-none"
                            placeholder={t('portfolioDashboard.overwrite.placeholder')}
                            value={comment}
                            onChange={(event) => setComment(event.target.value)}
                            onKeyDown={(event) => {
                                // The reason is usually one line; Enter should finish it rather
                                // than start a paragraph nobody wanted.
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault();
                                    if (comment.trim()) onConfirm(comment.trim());
                                }
                            }}
                        />
                    </div>
                ) : (
                    <></>
                )}

                <DialogFooter>
                    {/* Named for what it does, not "cancel": the change stands either way, and a
                        button that reads like an undo would be a lie about what pressing it does. */}
                    <Button type="button" variant="outline" onClick={onDismiss}>
                        {t('portfolioDashboard.overwrite.withoutReason')}
                    </Button>
                    <Button type="button" disabled={!comment.trim()} onClick={() => onConfirm(comment.trim())}>
                        {t('portfolioDashboard.overwrite.confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
